import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { trpc } from 'src/trpc';
import { DataTable } from 'src/components/table/DataTable';
import { SortingState } from '@tanstack/react-table';
import { Search } from 'src/components/Search';
import { useSearchParams } from 'react-router-dom';
import { createUserColumns } from 'src/pages/Settings/columns/UserColumns';
import { UserSheet } from 'src/pages/Settings/user/UserSheet';
import { useDebounce } from 'src/components/Debounce';
import { Button } from '@/components/ui/button';
import { User } from 'src/trpc';

const UserDashboard: React.FC = () => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [isUserSheetOpen, setIsUserSheetOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [params, setParams] = useSearchParams({
    offset: '0',
    limit: '25',
    search: '',
  });

  const [search, setSearch] = useState(params.get('search') ?? '');
  const debouncedSearch = useDebounce(search, 500);

  const offset = parseInt(params.get('offset') ?? '0');
  const limit = parseInt(params.get('limit') ?? '25');

  const userQuery = trpc.admin.user.adminGetAllUsers.useQuery(
    {
      offset,
      limit,
      search: params.get('search') ?? '',
    },
    {
      placeholderData: keepPreviousData,
    },
  );

  const updateSearchParams = useCallback(
    (searchValue: string) => {
      const newParams = new URLSearchParams(params);
      newParams.set('search', searchValue);
      newParams.set('offset', '0');
      setParams(newParams);
    },
    [params, setParams],
  );

  useEffect(() => {
    const currentSearch = params.get('search') ?? '';
    if (debouncedSearch !== currentSearch) {
      updateSearchParams(debouncedSearch);
    }
  }, [debouncedSearch, params, updateSearchParams]);

  const handleSearch = (value: string) => {
    setSearch(value);
  };

  const currentPage = offset === 0 ? 1 : offset / limit + 1;

  const handlePageChange = useCallback(
    (change: number) => {
      const newParams = new URLSearchParams(params);
      newParams.set('offset', (offset + change * limit).toString());
      setParams(newParams);
    },
    [offset, limit, params, setParams],
  );

  const handleEditUser = useCallback((user: User) => {
    setEditingUser(user);
    setIsUserSheetOpen(true);
  }, []);

  const handleCreateUser = useCallback(() => {
    setEditingUser(null);
    setIsUserSheetOpen(true);
  }, []);

  const handleCloseSheet = useCallback(() => {
    setIsUserSheetOpen(false);
    setEditingUser(null);
    userQuery.refetch();
  }, [userQuery]);

  const columns = useMemo(() => createUserColumns(handleEditUser), [handleEditUser]);

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold mb-4">Users</h2>
      <div className="flex justify-between items-center space-x-4">
        <div className="flex-grow">
          <Search
            value={search}
            onChange={handleSearch}
            placeholder="Search users by name, email, or phone"
            isLoading={userQuery.isLoading}
          />
        </div>
        <Button onClick={handleCreateUser}>New User</Button>
      </div>
      <UserSheet isOpen={isUserSheetOpen} onClose={handleCloseSheet} user={editingUser} />
      <div className="flex flex-col h-full">
        <DataTable
          columns={columns}
          data={userQuery.data?.users ?? []}
          sorting={sorting}
          onSortingChange={setSorting}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          pageCount={Math.ceil((userQuery.data?.totalCount ?? 0) / limit)}
          hasNextPage={(userQuery.data?.totalCount ?? 0) > offset + limit}
          hasPreviousPage={offset > 0}
          total={userQuery.data?.totalCount ?? 0}
          resourceName="users"
          offset={offset}
          limit={limit}
        />
      </div>
    </div>
  );
};

export default UserDashboard;
